import { useEffect, useState } from 'react';
import { Card } from '../../../components/Card/Card';
import { useInsightsState, useInsightsActions } from '../../../hooks/useInsights';
import { useTranslation } from '../../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../../types/constants';
import { TimePeriod } from '../../../types/insights';
import { ReportSectionLayout } from './ReportsSection';
import { ComplianceWarningBanner } from '../../../components/ComplianceWarningBanner/ComplianceWarningBanner';
import { isAdminDateOverrideEnabled, isReportingPaused } from '../../../utils/featureFlags';
import { StatusRoundel } from '../../../components/StatusRoundel/StatusRoundel';
import { ReportingPausedRoundel } from '../../../components/ReportingPaused/ReportingPausedRoundel';
import { ReportingPausedBanner } from '../../../components/ReportingPaused/ReportingPausedBanner';
import { useDebugActions } from '../../../hooks/useDebugActions';
import { convertUTCToLocalDate } from '../../../utils/dateUtils';

const PetszelStatCard = ({
  title,
  monthLabel,
  count,
  loading,
}: {
  title: string;
  monthLabel: string;
  count: number | null;
  loading: boolean;
}) => (
  <Card
    customBackground="bg-gray-50"
    radius="large"
    padding="large"
    height="h-[208px]"
    shadow
    className="flex-1 flex flex-col justify-between"
  >
    <div className="flex flex-col gap-1">
      <div className="text-base font-bold text-black-950">{title}</div>
      <div className="text-sm text-black-600">{monthLabel}</div>
    </div>
    <div className="text-8xl font-bold text-black/90 leading-none">
      {loading || count === null ? '0' : count}
    </div>
  </Card>
);

const SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const PetszelReportsSection = () => {
  const { t } = useTranslation();
  const { insights, isLoading } = useInsightsState();
  const { getDashboardInsights } = useInsightsActions();
  const { getDateOverride } = useDebugActions();
  const [currentDate, setCurrentDate] = useState(() => new Date());

  const dateOverrideEnabled = isAdminDateOverrideEnabled();

  useEffect(() => {
    getDashboardInsights({ period: TimePeriod.MONTH });
  }, [getDashboardInsights]);

  useEffect(() => {
    if (!dateOverrideEnabled) return;
    (async () => {
      try {
        const response = await getDateOverride();
        if (response.isActive && response.overrideDate) {
          const newDate = convertUTCToLocalDate(response.overrideDate);
          if (newDate) setCurrentDate(newDate);
        }
      } catch (e) {
        console.error('Failed to load date override:', e);
      }
    })();
  }, [getDateOverride, dateOverrideEnabled]);

  const monthName = currentDate.toLocaleString('en-US', { month: 'long' });
  const currentShortMonth = SHORT_MONTHS[currentDate.getMonth()];
  const currentYear = currentDate.getFullYear();

  const optInEntry = insights?.optInRate.monthlyData.find(
    (m) => m.month === currentShortMonth && m.year === currentYear
  );
  const adoptionEntry = insights?.adoptions.monthlyData.find(
    (m) => m.month === currentShortMonth && m.year === currentYear
  );

  const optIns: number | null =
    !optInEntry ||
    optInEntry.reportMissing ||
    (optInEntry.optIns === 0 && !optInEntry.hasZeroOptIns)
      ? null
      : optInEntry.optIns;
  const adoptions: number | null =
    !adoptionEntry ||
    adoptionEntry.reportMissing ||
    (adoptionEntry.total === 0 && !insights?.adoptions.hasCompleteData)
      ? null
      : adoptionEntry.total;

  const paused = isReportingPaused();
  const showComplianceWarning = !paused && insights?.complianceWarning?.show === true;
  const lastDataReceivedDate = insights?.lastDataReceivedDate;

  const statusRoundelNode = paused
    ? <ReportingPausedRoundel tooltipAlign="right" />
    : !showComplianceWarning
      ? <StatusRoundel align="right" lastDataReceivedDate={lastDataReceivedDate} />
      : undefined;

  return (
    <ReportSectionLayout
      title={t(TranslationKeyEnum.Reports)}
      description=""
      viewPastReportsUrl="/reports?tab=insights"
      viewPastReportsLabel={t(TranslationKeyEnum.ViewPastReportsAndInsights)}
      showTooltip
      tooltipAlign="right"
      statusRoundel={statusRoundelNode}
    >
      <div className="flex flex-col gap-6 w-full">
        <div className="flex flex-col lg:flex-row gap-6 w-full">
          <PetszelStatCard
            title={t(TranslationKeyEnum.PetParentsOptedIns)}
            monthLabel={t(TranslationKeyEnum.InMonth, monthName)}
            count={optIns}
            loading={isLoading}
          />
          <PetszelStatCard
            title={t(TranslationKeyEnum.Adoptions)}
            monthLabel={t(TranslationKeyEnum.InMonth, monthName)}
            count={adoptions}
            loading={isLoading}
          />
        </div>
        {showComplianceWarning && <ComplianceWarningBanner />}
        {paused && <ReportingPausedBanner />}
      </div>
    </ReportSectionLayout>
  );
};
